import { ReactElement, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import cn from 'classnames';

import styles from './Step9b.module.scss';
import cms from '../../../data/cms.json';
import { fetchResultData } from '../../../utils/externalDataHandling';
import { cellPhoneNumberValidationSchema } from '../../../utils/validations';

import { Step } from '../../Step/Step';
import { Box } from '../../Box/Box';
import { ErrorForm } from '../../ErrorForm/ErrorForm';
import { Button } from '../../Button/Button';
import { AppContext } from '../../Layout/Layout';
import { Title } from '../../Title/Title';
import { Paragraph } from '../../Paragraph/Paragraph';
import { ValuesProps } from '../Step6/Step6';
import PhoneNumberInputNew from '../../PhoneNumberInput/PhoneNumberInput';
import { triggerGtmEvent } from '../../../utils/triggerGtmEvent';
import { getPhonePrefix } from '../../../utils/getPhonePrefix';

interface Step9bProps {
    className?: string;
}

export const Step9b = ({ className }: Step9bProps): ReactElement | null => {
    const ctx = useContext(AppContext);
    const navigate = useNavigate();
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        ctx.setIsBackButtonVisible(false);
        triggerGtmEvent('offline');
    }, []);

    return (
        <div className={cn(styles.wrapper, className)}>
            <Formik
                validateOnBlur={false}
                validateOnChange={submitted}
                validateOnMount={false}
                initialValues={{
                    phone: ctx.formResult.Participants[0].Contact.Phone ?? '',
                    phonePrefix: ctx.formResult.Participants[0].Contact.PhonePrefix.Prefix ?? '',
                }}
                validationSchema={cellPhoneNumberValidationSchema}
                onSubmit={(values) => {
                    setLoading(true);
                    const storageData = localStorage.getItem('localData');

                    if (storageData) {
                        const body = {
                            PhonePrefixExternalId: getPhonePrefix(values.phonePrefix, ctx.initData)?.ExternalId,
                            PhoneNumber: values.phone.toString(),
                            OnlinePolicyJson: storageData,
                        };

                        const result = fetchResultData('api/online/support/save-phone-contact', body);
                        result
                            .then(() => {
                                setSuccess(true);
                                setLoading(false);
                            })
                            .catch((err) => {
                                console.error(err);
                                setError(true);
                                setLoading(false);
                            });
                    } else {
                        setError(true);
                    }
                }}
            >
                {({ errors, handleSubmit }: ValuesProps) => (
                    <Step title={cms.step9b.title} text={cms.step9b.text}>
                        <Box innerWrapper>
                            {error ? (
                                <div className={styles.result}>
                                    <Title tag="h2" className={cn('mb-3', styles.resultTitle)}>
                                        {cms.common.phoneConsultationErrorTitle}
                                    </Title>

                                    <Paragraph className={styles.resultText}>
                                        {cms.common.phoneConsultationErrorText}
                                    </Paragraph>

                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            setError(false);
                                            ctx.setPopupVisible(false);
                                            localStorage.clear();
                                            navigate('/');
                                            navigate(0);
                                        }}
                                    >
                                        {cms.common.closeButton}
                                    </Button>
                                </div>
                            ) : success ? (
                                <div className={styles.result}>
                                    <Title tag="h2" className={cn('mb-3', styles.resultTitle)}>
                                        {cms.common.phoneConsultationSuccessTitle}
                                    </Title>

                                    <Paragraph className={styles.resultText}>
                                        {cms.common.phoneConsultationSuccessText}
                                    </Paragraph>

                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            setSuccess(false);
                                            ctx.setPopupVisible(false);
                                            localStorage.clear();
                                            navigate('/');
                                            navigate(0);
                                        }}
                                    >
                                        {cms.common.closeButton}
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div className="mb-6">
                                        <PhoneNumberInputNew
                                            className="field-large"
                                            namePhone="phone"
                                            namePhonePrefix="phonePrefix"
                                            label="Vyplňte vaše číslo a my se vám ozveme"
                                            showEditIcon
                                            error={errors.phone}
                                            optional={false}
                                        />
                                    </div>

                                    <Button
                                        variant="primary"
                                        loading={loading}
                                        onClick={() => {
                                            triggerGtmEvent('offline_go');
                                            setSubmitted(true);
                                            handleSubmit();
                                        }}
                                    >
                                        {cms.common.submitButton}
                                    </Button>
                                </>
                            )}
                        </Box>
                        <ErrorForm />
                    </Step>
                )}
            </Formik>
        </div>
    );
};
